/**
 * Tempo relativo ("há 3 min") + carimbo exato para o `title`.
 *
 * Por que os dois SEMPRE juntos: "há 3 minutos" responde rápido a pergunta que
 * o operador faz de verdade ("isso está vivo?"), mas some quando ele precisa
 * comparar com o horário de um anúncio ou de um print. O exato fica no `title`
 * — visível no hover e lido por leitor de tela — e nunca no fuso do navegador:
 * o dia do produto é o do workspace (invariante 10 do CLAUDE.md).
 *
 * Sobre o tipo de entrada: sem transformer no tRPC, o `Date` do servidor chega
 * como STRING ISO — e o tipo inferido do cliente já reflete isso. As funções
 * aceitam `Date | string | number` mesmo assim, para servirem também a datas
 * criadas no próprio front (o `now` injetado, por exemplo).
 */
import { formatDate } from '@mgdlead/ui';

export type DateInput = Date | string | number | null | undefined;

const RELATIVE = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });

/** Abaixo disso não vale dizer "há 0 minutos". */
const AGORA_MS = 45_000;

const MINUTO = 60_000;
const HORA = 60 * MINUTO;
const DIA = 24 * HORA;
const MES = 30 * DIA;

export function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * `"há 3 minutos"`, `"ontem"`, `"há 2 meses"`. `null` quando não há data —
 * quem chama decide o que mostrar no lugar (normalmente `NO_DATA_DASH`), porque
 * "nunca aconteceu" não é a mesma coisa que "aconteceu agora".
 *
 * `now` é parâmetro para o teste poder injetar o relógio.
 */
export function formatRelative(value: DateInput, now: Date = new Date()): string | null {
  const date = toDate(value);
  if (date === null) return null;

  // Relógio do cliente adiantado não pode virar "daqui a 4 minutos" num evento
  // que já aconteceu: o passado é o único sentido possível aqui.
  const elapsed = Math.max(0, now.getTime() - date.getTime());

  if (elapsed < AGORA_MS) return 'há instantes';
  if (elapsed < HORA) return RELATIVE.format(-Math.round(elapsed / MINUTO), 'minute');
  if (elapsed < DIA) return RELATIVE.format(-Math.round(elapsed / HORA), 'hour');
  if (elapsed < MES) return RELATIVE.format(-Math.round(elapsed / DIA), 'day');
  return RELATIVE.format(-Math.round(elapsed / MES), 'month');
}

/** Carimbo completo no fuso do workspace. Vai para o `title`, nunca sozinho. */
export function formatExact(value: DateInput, timeZone: string): string | null {
  const date = toDate(value);
  if (date === null) return null;

  return formatDate(date, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  });
}

/**
 * Duração em minutos/horas para texto. Usada só para EXIBIR o que o servidor
 * já mediu (`silentForMs`) — não decide nada.
 */
export function formatDuration(ms: number): string {
  if (ms < MINUTO) return 'menos de um minuto';
  if (ms < HORA) {
    const minutos = Math.floor(ms / MINUTO);
    return minutos === 1 ? '1 minuto' : `${minutos} minutos`;
  }
  if (ms < DIA) {
    const horas = Math.floor(ms / HORA);
    return horas === 1 ? '1 hora' : `${horas} horas`;
  }
  const dias = Math.floor(ms / DIA);
  return dias === 1 ? '1 dia' : `${dias} dias`;
}
